/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect } from 'react';

import Imgix from 'js/components/Imgix';

import { useTracker } from '@coursera/event-pulse/react';

import BaseSelectQuestion from 'bundles/onboarding-common/components/modular/BaseSelectQuestion';
import SelectQuestionLoadingState from 'bundles/onboarding-common/components/modular/SelectQuestionLoadingState';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useArtificiallyPaginatedRoles from 'bundles/onboarding-common/hooks/useArtificiallyPaginatedRoles';
import useFormEventingData from 'bundles/onboarding-common/hooks/useFormEventingData';
import useGetDefaultSavedRoles from 'bundles/onboarding-common/hooks/useGetDefaultSavedRoles';
import useHasIndicatedUnsureOfRoleOrSkills from 'bundles/onboarding-common/hooks/useHasIndicatedUnsureOfRoleOrSkills';
import useRoleSelectQuestionState from 'bundles/onboarding-common/hooks/useRoleSelectQuestionState';
import type { RoleFragment } from 'bundles/onboarding-common/queries/__generated__/RoleFragment';
import type { Role, RoleWithState, SelectOptionWithState } from 'bundles/onboarding-common/types';
import { getIdontKnowOption } from 'bundles/onboarding-common/utils';

import _t from 'i18n!nls/onboarding-common';

const RoleSelectQuestion = ({
  defaultValue,
  roles,
  defaultSingleSelectOptionIsSelected,
}: {
  defaultValue: RoleWithState[];
  roles: RoleFragment[];
  defaultSingleSelectOptionIsSelected: boolean;
}) => {
  const {
    containerRef,
    rolesPerRow,
    iDontKnowRole,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
    shouldUseDefaultLayout,
    hasShownAllItems,
  } = useRoleSelectQuestionState({ defaultValue, roles, defaultSingleSelectOptionIsSelected });
  const track = useTracker();
  const { formEventData } = useFormEventingData();

  const handleRoleClick = (item: SelectOptionWithState<Role>) => {
    handleClick(item);
    track('click_checkbox', {
      form: formEventData,
      checkbox: {
        name: 'career_role',
        label: item.name || '',
        checked: item.isSelected,
      },
    });
  };

  return (
    <BaseSelectQuestion
      itemsPerRow={rolesPerRow}
      containerRef={containerRef}
      handleClick={handleRoleClick}
      onViewMoreClick={onViewMoreClick}
      onViewAllClick={onViewAllClick}
      isLoading={isLoading}
      isExpanded={isExpanded}
      shouldUseDefaultLayout={shouldUseDefaultLayout}
      getLabel={(item) => item.name || ''}
      getIcon={(item) =>
        item.id !== iDontKnowRole?.id ? (
          <Imgix src={item.onboardingRoleOptionImage?.url || ''} width={16} height={16} alt="" />
        ) : undefined
      }
      getLabelSpacing={(item) => (item.id === iDontKnowRole?.id ? 'small' : 'medium')}
      ariaLabel={_t('Select your role(s)')}
      hasShownAllItems={hasShownAllItems}
    />
  );
};

const RoleSelectQuestionWrapper = () => {
  const { setQuestionValue } = useOnboardingQuestionData();
  const { defaultValue, isLoadingDefaultRoles } = useGetDefaultSavedRoles({});
  const { hasIndicatedUnsureOfRole, loading: isLoadingIsUnsureOfRoles } = useHasIndicatedUnsureOfRoleOrSkills();
  const { roles, loading: isLoadingRoles } = useArtificiallyPaginatedRoles({ skip: 0, limit: 20 });

  useEffect(() => {
    if (defaultValue?.length > 0) {
      setQuestionValue({
        __typename: QUESTION_IDS.roleSelect,
        value: defaultValue.map((role) => ({
          id: role.id,
          name: role.name || '',
          type: role.type,
        })),
      });
    } else if (hasIndicatedUnsureOfRole) {
      const { id, name, type } = getIdontKnowOption();
      setQuestionValue({
        __typename: QUESTION_IDS.roleSelect,
        value: [{ id, name, type }],
      });
    }
  }, [defaultValue, hasIndicatedUnsureOfRole, setQuestionValue]);

  if (isLoadingDefaultRoles || isLoadingRoles || isLoadingIsUnsureOfRoles) {
    return <SelectQuestionLoadingState />;
  }

  return (
    <RoleSelectQuestion
      defaultValue={defaultValue}
      roles={roles}
      defaultSingleSelectOptionIsSelected={!defaultValue.length && hasIndicatedUnsureOfRole}
    />
  );
};

export default RoleSelectQuestionWrapper;
